import { NexpimView } from "../../views";
import { makeConfigPage } from "./makeConfigPage";
import { isoDate } from "../../api/reporting";

/* ---------------------------------------------------------------- types --- */

interface DesignerDTO {
  id: number;
  name: string;
  royalty: number;
}

interface NamedConfigDTO {
  id: number;
  name: string;
}

interface CogsExpenseDTO {
  id: number;
  production_labor: number;
  production_overhead: number;
  assembly_labor: number;
  assembly_overhead: number;
  date_from: string;
  date_to?: string | null;
}

/* -------------------------------------------------------------- helpers --- */

const numberField = <T,>(key: keyof T & string) => ({
  kind: "number" as const,
  get: (row: T) => row[key] as number,
  set: (row: T, value: string) => ({ ...row, [key]: value === "" ? 0 : Number(value) }),
});

const textField = <T,>(key: keyof T & string) => ({
  kind: "text" as const,
  get: (row: T) => (row[key] as string) ?? "",
  set: (row: T, value: string) => ({ ...row, [key]: value }),
  required: true,
});

const named = (path: string, cacheKey: string, titleKey: string, fallback: string, view: string) =>
  makeConfigPage<NamedConfigDTO>({
    path,
    cacheKey,
    titleKey,
    titleFallback: fallback,
    view,
    columns: (c) => [c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 })],
    editor: {
      fields: { name: textField<NamedConfigDTO>("name") },
      newRow: () => ({ id: 0, name: "" }),
      validate: (row): Record<string, string> => (row.name.trim() ? {} : { name: "Required" }),
      canDelete: true,
    },
  });

/* ---------------------------------------------------------------- pages --- */

/** Designer roster; royalty feeds the royalty PDF export. */
export const DesignerPage = makeConfigPage<DesignerDTO>({
  path: "/product/designer",
  cacheKey: "designer",
  titleKey: "navigation.item.config-designer",
  titleFallback: "Product designers",
  view: NexpimView.CONFIG_PRODUCT_DESIGNER,
  columns: (c) => [
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
    c.number("royalty", (row) => row.royalty, { headerKey: "config.royalty", priority: 2 }),
  ],
  editor: {
    fields: {
      name: textField<DesignerDTO>("name"),
      royalty: numberField<DesignerDTO>("royalty"),
    },
    newRow: () => ({ id: 0, name: "", royalty: 0 }),
    validate: (row): Record<string, string> => (row.name.trim() ? {} : { name: "Required" }),
    canDelete: true,
  },
});

export const CollectionPage = named(
  "/product/collection", "collection", "navigation.item.config-collection", "Product collections",
  NexpimView.CONFIG_PRODUCT_COLLECTION,
);
export const CategoryPage = named(
  "/product/category", "category", "navigation.item.config-category", "Product categories",
  NexpimView.CONFIG_PRODUCT_CATEGORY,
);
export const TagPage = named(
  "/product/tag", "tag", "navigation.item.config-tag", "Product tags",
  NexpimView.CONFIG_PRODUCT_TAG,
);
export const MetafieldConfigPage = named(
  "/metafield", "metafield-def", "navigation.item.config-metafield", "Product metafields",
  NexpimView.CONFIG_PRODUCT_METAFIELD,
);

/** Overhead expense lines feeding the COGS computation. */
export const CogsExpensePage = makeConfigPage<CogsExpenseDTO>({
  path: "/cogs/expense",
  cacheKey: "cogs-expense",
  titleKey: "navigation.item.config-cogs-expense",
  titleFallback: "COGS expenses",
  view: NexpimView.CONFIG_COGS_EXPENSE,
  columns: (c) => [
    // The Vaadin grid showed the period as year + month; the raw dates stay
    // alongside because a period doesn't have to be a whole calendar month.
    c.text("year", (row) => (row.date_from ?? "").slice(0, 4), {
      headerKey: "column.year",
      filter: "select",
      priority: 1,
    }),
    c.text("month", (row) => monthName(row.date_from), {
      headerKey: "column.month",
      filter: "select",
      priority: 1,
    }),
    c.text("date_from", (row) => row.date_from, { headerKey: "period.from", priority: 3 }),
    c.text("date_to", (row) => row.date_to ?? "", { headerKey: "period.to", priority: 3 }),
    c.number("production_labor", (row) => row.production_labor, { headerKey: "product.cogs.prod-labor" }),
    c.number("production_overhead", (row) => row.production_overhead, { headerKey: "product.cogs.prod-overhead" }),
    c.number("assembly_labor", (row) => row.assembly_labor, { headerKey: "product.cogs.asm-labor" }),
    c.number("assembly_overhead", (row) => row.assembly_overhead, { headerKey: "product.cogs.asm-overhead" }),
  ],
  editor: {
    fields: {
      date_from: textField<CogsExpenseDTO>("date_from"),
      date_to: {
        kind: "text",
        get: (row) => row.date_to ?? "",
        set: (row, value) => ({ ...row, date_to: value || null }),
      },
      production_labor: numberField<CogsExpenseDTO>("production_labor"),
      production_overhead: numberField<CogsExpenseDTO>("production_overhead"),
      assembly_labor: numberField<CogsExpenseDTO>("assembly_labor"),
      assembly_overhead: numberField<CogsExpenseDTO>("assembly_overhead"),
    },
    newRow: () => ({
      id: 0,
      production_labor: 0,
      production_overhead: 0,
      assembly_labor: 0,
      assembly_overhead: 0,
      date_from: isoDate(new Date()),
      date_to: null,
    }),
    canDelete: true,
  },
});

/** The month name of a period's start date, for the COGS-expense grid. */
function monthName(date: string | null | undefined): string {
  if (!date) return "";
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? ""
    : parsed.toLocaleString(undefined, { month: "long" });
}
